import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useOrders } from '../context/OrderContext';
import { useMenu } from '../context/MenuContext';
import './ThermalPrinter.css';

const ThermalPrinter = () => {
  const [searchParams] = useSearchParams();
  const { orders, loading } = useOrders();
  const { menuItems } = useMenu();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [printType, setPrintType] = useState('bill'); // bill, kot, menu, report
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [orderNotFound, setOrderNotFound] = useState(false);

  // Read orderId from URL params and auto-select order
  useEffect(() => {
    const orderIdFromUrl = searchParams.get('orderId');
    console.log('📊 ThermalPrinter - orderId from URL:', orderIdFromUrl);
    console.log('📊 ThermalPrinter - orders loaded:', orders.length);
    console.log('📊 ThermalPrinter - loading:', loading);

    if (orderIdFromUrl) {
      if (loading) {
        console.log('⏳ Still loading orders...');
        return;
      }

      if (orders.length > 0) {
        const order = orders.find(o => o.id === orderIdFromUrl);
        console.log('🔍 Order found:', order ? 'YES' : 'NO');

        if (order) {
          console.log('✅ Setting selected order:', order);
          setSelectedOrder(order);
          setOrderNotFound(false);
          // Auto-print after a short delay to allow render
          setTimeout(() => {
            console.log('🖨️ Auto-printing...');
            window.print();
          }, 1000);
        } else {
          console.error('❌ Order not found with ID:', orderIdFromUrl);
          setOrderNotFound(true);
        }
      } else {
        console.log('⚠️ No orders available');
        setOrderNotFound(true);
      }
    }
  }, [searchParams, orders, loading]);

  // Format date
  const formatDate = (date) => {
    if (!date) return '';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get orders for selected date
  const getOrdersForDate = () => {
    const selected = new Date(selectedDate);
    selected.setHours(0, 0, 0, 0);
    const nextDay = new Date(selected);
    nextDay.setDate(nextDay.getDate() + 1);

    return orders.filter(order => {
      const orderDate = order.timestamp.toDate ? order.timestamp.toDate() : new Date(order.timestamp);
      return orderDate >= selected && orderDate < nextDay;
    });
  };

  // Print function
  const handlePrint = () => {
    window.print();
  };

  // Render Bill Receipt
  const renderBillReceipt = (order) => {
    if (!order) return null;

    return (
      <div className="thermal-receipt">
        <div className="thermal-header">
          <h1>ODISHA ZAIKA</h1>
          <p>Authentic Odia Cuisine</p>
          <p>Ph: 7873611363</p>
          <div className="divider"></div>
        </div>

        <div className="thermal-body">
          <div className="receipt-info">
            <div className="info-row">
              <span>Order #:</span>
              <span><strong>{order.orderNumber || order.id.slice(-6)}</strong></span>
            </div>
            <div className="info-row">
              <span>Date:</span>
              <span>{formatDate(order.timestamp)}</span>
            </div>
            <div className="info-row">
              <span>Type:</span>
              <span>{order.orderType?.toUpperCase()}</span>
            </div>
            <div className="info-row">
              <span>Status:</span>
              <span>{order.status?.toUpperCase()}</span>
            </div>
          </div>

          <div className="divider"></div>

          {/* Customer Details */}
          {order.customer && (
            <>
              <div className="customer-section">
                <h3>CUSTOMER DETAILS</h3>
                <p><strong>{order.customer.name}</strong></p>
                <p>Ph: {order.customer.phone}</p>
                {order.customer.address && <p>{order.customer.address}</p>}
                {order.customer.location && <p>{order.customer.location}</p>}
              </div>
              <div className="divider"></div>
            </>
          )}

          {/* Order Items */}
          <div className="items-section">
            <div className="items-header">
              <span>Item</span>
              <span>Qty</span>
              <span>Price</span>
            </div>
            <div className="divider-thin"></div>
            {order.items && order.items.map((item, index) => (
              <div key={index} className="item-row">
                <div className="item-name">{item.name}</div>
                <div className="item-details">
                  <span>{item.quantity}</span>
                  <span>₹{item.price * item.quantity}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="divider"></div>

          {/* Pricing */}
          <div className="pricing-section">
            <div className="price-row">
              <span>Subtotal:</span>
              <span>₹{order.pricing?.subtotal || 0}</span>
            </div>
            {order.pricing?.deliveryCharge > 0 && (
              <div className="price-row">
                <span>Delivery:</span>
                <span>₹{order.pricing.deliveryCharge}</span>
              </div>
            )}
            {order.pricing?.discount > 0 && (
              <div className="price-row">
                <span>Discount:</span>
                <span>-₹{order.pricing.discount}</span>
              </div>
            )}
            {order.pricing?.tax > 0 && (
              <div className="price-row">
                <span>Tax:</span>
                <span>₹{order.pricing.tax}</span>
              </div>
            )}
            <div className="divider-thin"></div>
            <div className="price-row total">
              <span><strong>TOTAL:</strong></span>
              <span><strong>₹{order.pricing?.total || 0}</strong></span>
            </div>
          </div>

          <div className="divider"></div>

          {/* Payment Details */}
          {order.payment && (
            <div className="payment-section">
              <div className="info-row">
                <span>Payment:</span>
                <span>{order.payment.method?.toUpperCase()}</span>
              </div>
              <div className="info-row">
                <span>Status:</span>
                <span>{order.payment.status?.toUpperCase()}</span>
              </div>
            </div>
          )}
        </div>

        <div className="thermal-footer">
          <div className="divider"></div>
          <p>THANK YOU!</p>
          <p>Visit Again</p>
          <p className="footer-note">Takeaway orders via WhatsApp: 7873611363</p>
        </div>
      </div>
    );
  };

  // Render KOT (Kitchen Order Ticket)
  const renderKOT = (order) => {
    if (!order) return null;

    return (
      <div className="thermal-receipt kot">
        <div className="thermal-header">
          <h1>KITCHEN ORDER TICKET</h1>
          <div className="divider"></div>
        </div>

        <div className="thermal-body">
          <div className="kot-info">
            <div className="info-row">
              <span>Order #:</span>
              <span><strong>{order.orderNumber || order.id.slice(-6)}</strong></span>
            </div>
            <div className="info-row">
              <span>Time:</span>
              <span>{formatDate(order.timestamp)}</span>
            </div>
            <div className="info-row">
              <span>Type:</span>
              <span><strong>{order.orderType?.toUpperCase()}</strong></span>
            </div>
          </div>

          <div className="divider"></div>

          {/* Items for Kitchen */}
          <div className="kot-items">
            <h3>ITEMS TO PREPARE</h3>
            <div className="divider-thin"></div>
            {order.items && order.items.map((item, index) => (
              <div key={index} className="kot-item">
                <div className="kot-item-header">
                  <span className="kot-qty">×{item.quantity}</span>
                  <span className="kot-item-name"><strong>{item.name}</strong></span>
                </div>
                {item.size && (
                  <div className="kot-item-note">Size: {item.size}</div>
                )}
                {item.addons && item.addons.length > 0 && (
                  <div className="kot-item-note">
                    Add-ons: {item.addons.map(a => a.name).join(', ')}
                  </div>
                )}
                {item.notes && (
                  <div className="kot-item-note">
                    Note: {item.notes}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="divider"></div>

          {/* Special Instructions */}
          {order.specialInstructions && (
            <>
              <div className="special-instructions">
                <h3>SPECIAL INSTRUCTIONS:</h3>
                <p>{order.specialInstructions}</p>
              </div>
              <div className="divider"></div>
            </>
          )}

          <div className="kot-footer-note">
            <p><strong>TABLE/TOKEN: {order.tableNumber || 'N/A'}</strong></p>
          </div>
        </div>
      </div>
    );
  };

  // Render Menu List
  const renderMenuList = () => {
    const groupedMenu = menuItems.reduce((acc, item) => {
      const category = item.category || 'Other';
      if (!acc[category]) {
        acc[category] = { veg: [], nonVeg: [] };
      }
      if (item.isVeg) {
        acc[category].veg.push(item);
      } else {
        acc[category].nonVeg.push(item);
      }
      return acc;
    }, {});

    return (
      <div className="thermal-receipt menu-list">
        <div className="thermal-header">
          <h1>ODISHA ZAIKA</h1>
          <h2>MENU LIST</h2>
          <p>{new Date().toLocaleDateString('en-IN')}</p>
          <div className="divider"></div>
        </div>

        <div className="thermal-body">
          {Object.keys(groupedMenu).sort().map(category => (
            <div key={category} className="menu-category">
              <h3 className="category-title">{category.toUpperCase()}</h3>
              <div className="divider-thin"></div>

              {/* Veg Items */}
              {groupedMenu[category].veg.length > 0 && (
                <div className="menu-section">
                  <p className="section-label">🟢 VEGETARIAN</p>
                  {groupedMenu[category].veg.map((item, idx) => (
                    <div key={idx} className="menu-item-row">
                      <span className="menu-item-name">{item.name}</span>
                      <span className="menu-item-price">
                        ₹{item.sizes && item.sizes[0] ? item.sizes[0].price : item.price || 0}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Non-Veg Items */}
              {groupedMenu[category].nonVeg.length > 0 && (
                <div className="menu-section">
                  <p className="section-label">🔴 NON-VEGETARIAN</p>
                  {groupedMenu[category].nonVeg.map((item, idx) => (
                    <div key={idx} className="menu-item-row">
                      <span className="menu-item-name">{item.name}</span>
                      <span className="menu-item-price">
                        ₹{item.sizes && item.sizes[0] ? item.sizes[0].price : item.price || 0}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div className="divider"></div>
            </div>
          ))}
        </div>

        <div className="thermal-footer">
          <p>Total Items: {menuItems.length}</p>
          <p>Takeaway: 7873611363</p>
        </div>
      </div>
    );
  };

  // Render Daily Report
  const renderDailyReport = () => {
    const todayOrders = getOrdersForDate();
    const totalRevenue = todayOrders.reduce((sum, order) =>
      sum + (order.pricing?.total || 0), 0
    );
    const deliveredOrders = todayOrders.filter(o => o.status === 'delivered');
    const cancelledOrders = todayOrders.filter(o => o.status === 'cancelled');
    const deliveryOrders = todayOrders.filter(o => o.orderType === 'delivery');
    const pickupOrders = todayOrders.filter(o => o.orderType === 'pickup');

    return (
      <div className="thermal-receipt report">
        <div className="thermal-header">
          <h1>DAILY SALES REPORT</h1>
          <p>{new Date(selectedDate).toLocaleDateString('en-IN')}</p>
          <div className="divider"></div>
        </div>

        <div className="thermal-body">
          {/* Summary Stats */}
          <div className="report-section">
            <h3>SUMMARY</h3>
            <div className="divider-thin"></div>
            <div className="report-row">
              <span>Total Orders:</span>
              <span><strong>{todayOrders.length}</strong></span>
            </div>
            <div className="report-row">
              <span>Delivered:</span>
              <span>{deliveredOrders.length}</span>
            </div>
            <div className="report-row">
              <span>Cancelled:</span>
              <span>{cancelledOrders.length}</span>
            </div>
            <div className="report-row">
              <span>Delivery Orders:</span>
              <span>{deliveryOrders.length}</span>
            </div>
            <div className="report-row">
              <span>Pickup Orders:</span>
              <span>{pickupOrders.length}</span>
            </div>
          </div>

          <div className="divider"></div>

          {/* Revenue */}
          <div className="report-section">
            <h3>REVENUE</h3>
            <div className="divider-thin"></div>
            <div className="report-row total">
              <span>Total Revenue:</span>
              <span><strong>₹{totalRevenue}</strong></span>
            </div>
            <div className="report-row">
              <span>Avg Order Value:</span>
              <span>₹{todayOrders.length > 0 ? Math.round(totalRevenue / todayOrders.length) : 0}</span>
            </div>
          </div>

          <div className="divider"></div>

          {/* Order List */}
          <div className="report-section">
            <h3>ORDERS</h3>
            <div className="divider-thin"></div>
            {todayOrders.map((order, index) => (
              <div key={index} className="report-order-item">
                <div className="report-row">
                  <span>#{order.orderNumber || order.id.slice(-6)}</span>
                  <span>₹{order.pricing?.total || 0}</span>
                </div>
                <div className="report-order-details">
                  <span>{order.status} · {order.orderType}</span>
                  <span>{new Date(order.timestamp.toDate ? order.timestamp.toDate() : order.timestamp).toLocaleTimeString('en-IN', {hour: '2-digit', minute: '2-digit'})}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="thermal-footer">
          <div className="divider"></div>
          <p>ODISHA ZAIKA</p>
          <p>{new Date().toLocaleTimeString('en-IN')}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="thermal-printer-page">
      <div className="no-print">
        <h1 className="page-title">Thermal Printer</h1>
        <p className="page-subtitle">Print bills, KOT, menu, and reports</p>

        {/* Loading State */}
        {loading && (
          <div style={{ padding: '20px', textAlign: 'center', color: '#FF6B35' }}>
            <p>⏳ Loading orders...</p>
          </div>
        )}

        {/* Order Not Found Error */}
        {orderNotFound && searchParams.get('orderId') && (
          <div style={{
            padding: '20px',
            margin: '20px 0',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '8px',
            color: '#c00'
          }}>
            <h3>❌ Order Not Found</h3>
            <p>Could not find order with ID: <strong>{searchParams.get('orderId')}</strong></p>
            <p>Please select an order from the list below.</p>
          </div>
        )}

        {/* Print Type Selector */}
        <div className="print-controls">
          <div className="control-group">
            <label>Print Type:</label>
            <div className="button-group">
              <button
                className={`btn-select ${printType === 'bill' ? 'active' : ''}`}
                onClick={() => setPrintType('bill')}
              >
                📄 Customer Bill
              </button>
              <button
                className={`btn-select ${printType === 'kot' ? 'active' : ''}`}
                onClick={() => setPrintType('kot')}
              >
                🍳 Kitchen Order (KOT)
              </button>
              <button
                className={`btn-select ${printType === 'menu' ? 'active' : ''}`}
                onClick={() => setPrintType('menu')}
              >
                📋 Menu List
              </button>
              <button
                className={`btn-select ${printType === 'report' ? 'active' : ''}`}
                onClick={() => setPrintType('report')}
              >
                📊 Daily Report
              </button>
            </div>
          </div>

          {/* Order Selector for Bill/KOT */}
          {(printType === 'bill' || printType === 'kot') && (
            <div className="control-group">
              <label>Select Order:</label>
              <select
                className="order-select"
                value={selectedOrder?.id || ''}
                onChange={(e) => {
                  const order = orders.find(o => o.id === e.target.value);
                  setSelectedOrder(order);
                }}
              >
                <option value="">-- Select an order --</option>
                {orders.slice(0, 50).map(order => (
                  <option key={order.id} value={order.id}>
                    #{order.orderNumber || order.id.slice(-6)} - {order.customer?.name} - ₹{order.pricing?.total} - {order.status}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Date Selector for Report */}
          {printType === 'report' && (
            <div className="control-group">
              <label>Select Date:</label>
              <input
                type="date"
                className="date-input"
                value={selectedDate.toISOString().split('T')[0]}
                onChange={(e) => setSelectedDate(new Date(e.target.value))}
              />
            </div>
          )}

          {/* Print Button */}
          <button
            className="btn-print"
            onClick={handlePrint}
            disabled={
              (printType === 'bill' || printType === 'kot') && !selectedOrder
            }
          >
            🖨️ Print
          </button>
        </div>

        {/* Preview */}
        <div className="preview-section">
          <h2>Preview</h2>
          <p className="preview-note">
            This is how your receipt will look on thermal printer (80mm width)
          </p>
        </div>
      </div>

      {/* Print Area */}
      <div className="print-area">
        {printType === 'bill' && renderBillReceipt(selectedOrder)}
        {printType === 'kot' && renderKOT(selectedOrder)}
        {printType === 'menu' && renderMenuList()}
        {printType === 'report' && renderDailyReport()}
      </div>
    </div>
  );
};

export default ThermalPrinter;
